import { atom } from 'jotai';
import { loadable } from 'jotai/utils';
import { 
  fetchPrices, 
  getCurrentSubscription,
  createCheckoutSession,
  cancelSubscription,
  resumeSubscription,
  checkIsPremium,
} from '../../services/subscriptionService';
import {
  ProductPrices,
  PriceInfo,
  SUBSCRIPTION_PLAN,
  SUBSCRIPTION_INTERVAL,
  SubscriptionStatusType
} from '../../constants/subscription';

// 订阅状态类型定义
type Subscription = {
  id: string;
  status: SubscriptionStatusType;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
};

// 订阅价格状态
export const subscriptionPricesAtom = atom<ProductPrices | null>(null);
export const subscriptionPricesLoadingAtom = atom(false);
export const subscriptionPricesErrorAtom = atom<string | null>(null);

// 订阅状态
export const subscriptionAtom = atom<Subscription | null>(null);
export const subscriptionLoadingAtom = atom(false);
export const subscriptionErrorAtom = atom<string | null>(null);

// 是否为会员
export const isPremiumAtom = atom<boolean>(false);

// 订阅价格加载状态 - 使用loadable实现
export const loadableSubscriptionPricesAtom = loadable(
  atom(async (get) => {
    // 如果已有价格数据，则不重新加载
    const prices = get(subscriptionPricesAtom);
    if (prices) {
      return prices;
    }
    
    return await fetchPrices();
  })
);

// 订阅状态加载 - 使用loadable实现
export const loadableSubscriptionAtom = loadable(
  atom(async (get) => {
    // 如果已有订阅数据，则不重新加载
    const subscription = get(subscriptionAtom);
    if (subscription) {
      return subscription;
    }
    
    return await getCurrentSubscription();
  })
);

// 获取价格
export const fetchSubscriptionPricesAtom = atom(
  null,
  async (get, set) => {
    try {
      set(subscriptionPricesLoadingAtom, true);
      set(subscriptionPricesErrorAtom, null);
      
      const prices = await fetchPrices();
      set(subscriptionPricesAtom, prices);
      return prices;
    } catch (error: any) {
      set(subscriptionPricesErrorAtom, error.message || 'Failed to fetch subscription prices');
      console.error('Error fetching subscription prices:', error);
      return null;
    } finally {
      set(subscriptionPricesLoadingAtom, false);
    }
  }
);

// 根据计划和周期获取价格信息
export const getPriceByPlanAndIntervalAtom = atom(
  (get) => (planId = SUBSCRIPTION_PLAN.PLUS, interval = SUBSCRIPTION_INTERVAL.MONTH): PriceInfo | null => {
    const prices = get(subscriptionPricesAtom);
    if (!prices || !prices[planId] || !prices[planId][interval]) {
      return null;
    }
    return prices[planId][interval];
  }
);

// 获取订阅状态
export const fetchSubscriptionAtom = atom(
  null,
  async (get, set) => {
    try {
      set(subscriptionLoadingAtom, true);
      set(subscriptionErrorAtom, null);
      
      const subscription = await getCurrentSubscription();
      set(subscriptionAtom, subscription);
      
      // 同时更新会员状态
      const isPremium = await checkIsPremium();
      set(isPremiumAtom, isPremium);
      
      return subscription;
    } catch (error: any) {
      set(subscriptionErrorAtom, error.message || 'Failed to fetch subscription');
      console.error('Error fetching subscription:', error);
      return null;
    } finally {
      set(subscriptionLoadingAtom, false);
    }
  }
);

// 创建结账会话
export const createCheckoutSessionAtom = atom(
  null,
  async (get, set, priceId: string) => {
    try {
      set(subscriptionLoadingAtom, true);
      set(subscriptionErrorAtom, null);
      
      const session = await createCheckoutSession(priceId);
      
      // 重定向到Stripe Checkout
      if (session.url) {
        window.location.href = session.url;
      }
      
      return session;
    } catch (error: any) {
      set(subscriptionErrorAtom, error.message || 'Failed to create checkout session');
      console.error('Error creating checkout session:', error);
      return null;
    } finally {
      set(subscriptionLoadingAtom, false);
    }
  }
);

// 取消订阅
export const cancelSubscriptionAtom = atom(
  null,
  async (get, set, subscriptionId: string) => {
    try {
      set(subscriptionLoadingAtom, true);
      set(subscriptionErrorAtom, null);
      
      await cancelSubscription(subscriptionId);
      
      // 重新获取订阅状态
      const subscription = await getCurrentSubscription();
      set(subscriptionAtom, subscription);
      
      // 更新会员状态
      const isPremium = await checkIsPremium();
      set(isPremiumAtom, isPremium);
      
      return true;
    } catch (error: any) {
      set(subscriptionErrorAtom, error.message || 'Failed to cancel subscription');
      console.error('Error canceling subscription:', error);
      return false;
    } finally {
      set(subscriptionLoadingAtom, false);
    }
  }
);

// 恢复订阅
export const resumeSubscriptionAtom = atom(
  null,
  async (get, set, subscriptionId: string) => {
    try {
      set(subscriptionLoadingAtom, true);
      set(subscriptionErrorAtom, null);
      
      await resumeSubscription(subscriptionId);
      
      // 重新获取订阅状态
      const subscription = await getCurrentSubscription();
      set(subscriptionAtom, subscription);
      
      // 更新会员状态
      const isPremium = await checkIsPremium();
      set(isPremiumAtom, isPremium);
      
      return true;
    } catch (error: any) {
      set(subscriptionErrorAtom, error.message || 'Failed to resume subscription');
      console.error('Error resuming subscription:', error);
      return false;
    } finally {
      set(subscriptionLoadingAtom, false);
    }
  }
);

// 检查会员状态
export const checkIsPremiumAtom = atom(
  null,
  async (get, set) => {
    try {
      const isPremium = await checkIsPremium();
      set(isPremiumAtom, isPremium);
      return isPremium;
    } catch (error) {
      console.error('Error checking premium status:', error);
      return false;
    }
  }
); 